import { isMobile } from 'react-device-detect';

import imageUrlGenerator from './imageUrlGenerator';

const MUX_IMAGE_HOST = 'https://image.mux.com';
const MUX_POSTER_BASE_WIDTH = { mobile: 640, desktop: 1280 };
const MUX_POSTER_MAX_WIDTH = 1920;

const getDevicePixelRatio = () =>
  typeof window !== 'undefined' && typeof window.devicePixelRatio === 'number'
    ? window.devicePixelRatio
    : 1;

/**
 * A poster of the frame the video actually opens on.
 *
 * Mux defaults `time` to the middle of the asset, so a thumbnail requested
 * without it shows a different moment than frame 0 and visibly jumps the
 * instant playback starts — as does a curated preview picture. Pinning time=0
 * makes the still and the first decoded frame the same image.
 *
 * `width` is ours to choose because the URL is deterministic and served with a
 * week of cache — the one part of the Mux delivery chain the browser cache can
 * actually reuse, since the HLS manifests come back no-store.
 *
 * Expects a public playback ID. A signed one would need a thumbnail token.
 */
export const buildMuxPosterUrl = (playbackId) => {
  if (!playbackId) {
    return null;
  }

  const base = isMobile ? MUX_POSTER_BASE_WIDTH.mobile : MUX_POSTER_BASE_WIDTH.desktop;
  const width = Math.min(Math.round(base * getDevicePixelRatio()), MUX_POSTER_MAX_WIDTH);

  return `${MUX_IMAGE_HOST}/${playbackId}/thumbnail.webp?time=0&width=${width}`;
};

const ABSOLUTE_URL_REGEX = /^https?:\/\//i;

const normalizePictureInput = (input) => {
  if (!input) {
    return null;
  }

  if (typeof input === 'string') {
    return input;
  }

  if (typeof input === 'object') {
    if (input.fileName) {
      return input.fileName;
    }

    if (input.url) {
      return input.url;
    }
  }

  return null;
};

/**
 * Resolve a stored preview picture to a poster URL, or null when there is
 * nothing to show. Returning null matters: `imageUrlGenerator` substitutes a
 * generic placeholder for empty input, which is not something we want painted
 * over a video.
 */
export const buildPosterUrl = (input, desktopSize = '640x360', mobileSize = '360x200') => {
  const normalized = normalizePictureInput(input);

  if (!normalized) {
    return null;
  }

  if (ABSOLUTE_URL_REGEX.test(normalized)) {
    return normalized;
  }

  return imageUrlGenerator(normalized, desktopSize, mobileSize);
};
